import type { DeliveryState } from '@theailab-dev/parchment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDeliveryStateLabel } from '../utils/deliveryStateLabels';
import { getDeliveryStateBadgeVariant } from '../utils/getDeliveryStateBadgeVariant';
import { TablePageLayout } from '@/components/Layout/TablePageLayout';
import { Badge, LoadingSpinner, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import { useDelivery } from '@/hooks/useDelivery';
import { formatDateTime } from '@/utils/dateTime';
import { getNationalFormatPhoneNumber } from '@/utils/get-national-format-phone-number';

export default function Deliveries() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);

  const { data, isLoading } = useDelivery({
    page: currentPage,
    limit: currentLimit,
  });

  const handlePageChange = (nextPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', nextPage.toString());
    setSearchParams(params);
  };

  const handleLimitChange = (newLimit: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('limit', newLimit.toString());
    params.set('page', '1');
    setSearchParams(params);
  };

  if (isLoading || !data) {
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
        <LoadingSpinner />
      </div>
    );
  }

  const { data: rows, meta } = data;
  const totalPages = meta.totalPages || 1;

  return (
    <TablePageLayout
      title="배송 관리"
      description="배송 목록을 조회합니다"
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={meta.total}
      limit={currentLimit}
      onLimitChange={handleLimitChange}>
      <Table>
        <TableHeader>
          <TableHead className="w-24 whitespace-nowrap">ID</TableHead>
          <TableHead className="whitespace-nowrap">상태</TableHead>
          <TableHead className="min-w-[120px]">수령인</TableHead>
          <TableHead className="min-w-[200px]">상품명</TableHead>
          <TableHead className="whitespace-nowrap">수령인 전화번호</TableHead>
          <TableHead className="min-w-[160px] whitespace-nowrap">주문 일시</TableHead>
          <TableHead className="min-w-[120px]">메모</TableHead>
        </TableHeader>
        <TableBody>
          {rows.map((delivery) => {
            const state = delivery.state as DeliveryState;
            const receiverPhone = (delivery.receiverPhone ?? '').trim();
            return (
              <TableRow
                key={delivery.id}
                onClick={() => navigate(`/dashboard/delivery/${delivery.id}`)}
                className="cursor-pointer">
                <TableCell>{delivery.id}</TableCell>
                <TableCell nowrap>
                  <Badge variant={getDeliveryStateBadgeVariant(state)} size="sm">
                    {getDeliveryStateLabel(state)}
                  </Badge>
                </TableCell>
                <TableCell>{delivery.receiverName}</TableCell>
                <TableCell className="max-w-[280px] truncate" title={delivery.orderTitle}>
                  {delivery.orderTitle ?? '-'}
                </TableCell>
                <TableCell>{receiverPhone ? getNationalFormatPhoneNumber(receiverPhone) : '-'}</TableCell>
                <TableCell nowrap>{formatDateTime(delivery.orderedAt)}</TableCell>
                <TableCell className="max-w-[240px] truncate" title={delivery.memo}>
                  {delivery.memo ?? '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TablePageLayout>
  );
}
